import React from 'react';
import { Calendar, ChevronRight, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import type { MatterDeadline } from '../../lib/db';

// Props interface for the DeadlineWidget component
// deadlines: Array of deadline objects to display
// title: Header text for the widget (defaults to 'Upcoming Deadlines')
// emptyMessage: Text to show when no deadlines exist
// showViewAll: Whether to show a "View all" link
// onViewAll: Function called when "View all" is clicked
// onDeadlineClick: Function called when a specific deadline is clicked
interface DeadlineWidgetProps {
  deadlines: MatterDeadline[];
  title?: string;
  emptyMessage?: string;
  showViewAll?: boolean;
  onViewAll?: () => void;
  onDeadlineClick?: (deadline: MatterDeadline) => void;
}

/**
 * DeadlineWidget Component
 * 
 * This component displays a list of upcoming deadlines in a compact widget format.
 * It's like a preview window that shows the most important deadlines at a glance,
 * with color coding to highlight urgency.
 * 
 * Features:
 * 1. Shows up to 5 deadlines with priority color coding
 * 2. Overdue deadlines are marked in red with warning icons
 * 3. Today's deadlines are marked in orange
 * 4. Future deadlines are shown in gray
 * 5. Clicking on a deadline can trigger navigation or actions
 * 6. "View all" link to see complete deadline list
 * 7. Empty state when no deadlines exist
 * 
 * Color coding:
 * - Red: Overdue deadlines (past due date)
 * - Orange: Due today
 * - Gray: Future deadlines
 */
export function DeadlineWidget({ 
  deadlines, 
  title = 'Upcoming Deadlines',
  emptyMessage = 'No upcoming deadlines',
  showViewAll = true,
  onViewAll,
  onDeadlineClick 
}: DeadlineWidgetProps) {
  // Helper function to check if a deadline is overdue (past its due date)
  const isOverdue = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);  // Set to midnight for accurate comparison
    return new Date(date) < today;
  };

  // Helper function to check if a deadline is due today
  const isToday = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);  // Start of today
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);  // Start of tomorrow
    const deadlineDate = new Date(date);
    // Check if deadline falls between start of today and start of tomorrow
    return deadlineDate >= today && deadlineDate < tomorrow;
  };

  // Helper function to get appropriate label text for the deadline date
  const getDateLabel = (date: Date) => {
    if (isToday(date)) return 'Today';
    if (isOverdue(date)) return 'Overdue';
    // For future dates, show abbreviated month and day (e.g., "Jan 15")
    return format(new Date(date), 'MMM d');
  };

  // Helper function to get appropriate CSS colors based on deadline urgency
  const getDateColor = (date: Date) => {
    if (isOverdue(date)) return 'text-red-600 bg-red-50';      // Red for overdue
    if (isToday(date)) return 'text-orange-600 bg-orange-50';  // Orange for today
    return 'text-gray-600 bg-gray-50';                         // Gray for future
  };

  return (
    // Main widget container with white background and shadow
    <div className="bg-white rounded-lg shadow">
      {/* Widget header with title and "View all" link */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {/* Title with calendar icon */}
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
            {title}
          </h3>
          {/* "View all" button - only show if enabled and deadlines exist */}
          {showViewAll && deadlines.length > 0 && (
            <button
              onClick={onViewAll}
              className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              View all
              <ChevronRight className="w-3 h-3 ml-1" />
            </button>
          )}
        </div>
      </div>

      {/* Main content area with deadline list */}
      <div className="divide-y divide-gray-100">
        {deadlines.length === 0 ? (
          // Empty state when no deadlines exist
          <div className="px-4 py-8 text-center">
            <Calendar className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">{emptyMessage}</p>
          </div>
        ) : (
          // Display up to 5 deadlines as clickable items
          deadlines.slice(0, 5).map((deadline) => (
            <button
              key={deadline.id}
              onClick={() => onDeadlineClick?.(deadline)}
              className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-start justify-between">
                {/* Left side: deadline title and context */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center">
                    {/* Warning icon for overdue deadlines */}
                    {isOverdue(deadline.due_date) && (
                      <AlertTriangle className="w-3 h-3 text-red-500 mr-1 flex-shrink-0" />
                    )}
                    {/* Deadline title */}
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {deadline.title}
                    </p>
                  </div>
                  {/* Matter and client context information */}
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {deadline.matter_name}
                    {deadline.client_name && ` • ${deadline.client_name}`}
                  </p>
                </div>
                {/* Right side: date badge with color coding */}
                <div className="ml-2 flex-shrink-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getDateColor(deadline.due_date)}`}>
                    {getDateLabel(deadline.due_date)}
                  </span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer showing count of additional deadlines if more than 5 exist */}
      {deadlines.length > 5 && (
        <div className="px-4 py-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            +{deadlines.length - 5} more deadlines
          </p>
        </div>
      )}
    </div>
  );
}